import { useEffect, useState, useMemo } from 'react'
import { styled } from '@mui/material/styles'
import Modal from '@mui/material/Modal'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import { debounce } from '@mui/material/utils'
import {
  useParams,
  useNavigate,
  useSearchParams,
  useSubmit,
  useRouteLoaderData,
  useActionData,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { adminParticipantsLoader } from '../../Admin.loader'
import { getOption, useParticipantStatusOptions, Options } from '../../../../utils'
import { LoaderData, User, Participants, ParticipantStatus } from '../../../../types'
import { useLazySearchUserQuery } from '../../../../services/user'
import { putParticipant } from '../../Admin.action'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 6 },
  boxShadow: 24,
  padding: { xs: 1, sm: 3 },
  width: { xs: '100%', sm: '90%' },
  maxWidth: '50rem',
  height: { xs: '100%', sm: '80%' },
  overflow: 'auto',
}

const Title = styled('h2')(() => ({
  marginBottom: 0,
}))
const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between',
  flex: '1 0 auto',
}))

const getDefaultValue = (
  participant: Participants<true> | undefined
): (Options & User) | undefined => {
  if (!participant) return undefined
  const res = {
    id: participant.user_id,
    first_name: participant.first_name,
    last_name: participant.last_name,
    is_staff: false,
    value: participant.user_id,
    label: `${participant.last_name} ${participant.first_name}`,
  } as Options & User
  return res
}

export default function EditParticipant() {
  const { t } = useTranslation('admin')
  const submit = useSubmit()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const actionData = useActionData() as LoaderData<ReturnType<typeof putParticipant>> | undefined
  const { currentParticipant, eventId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }
  const isNew = currentParticipant === 'new'

  const participantStatusOptions = useParticipantStatusOptions(t)
  const data = useRouteLoaderData('participants') as LoaderData<
    ReturnType<typeof adminParticipantsLoader>
  >
  const [searchUser, lastArg] = useLazySearchUserQuery()

  const [selectedRegistration, setSelectedRegistration] = useState(
    isNew ? undefined : data?.participants?.results.find(el => el.id === Number(currentParticipant))
  )
  useEffect(() => {
    if (isNew) {
      setSelectedRegistration(undefined)
    } else {
      setSelectedRegistration(
        data?.participants?.results.find(el => el.id === Number(currentParticipant))
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentParticipant])

  useEffect(() => {
    if (actionData && 'data' in actionData) {
      navigate(`/admin/details/${eventId}/participants?${searchParams.toString()}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionData])

  const [currentUser, setCurrentUser] = useState<(Options & User) | undefined>(
    getDefaultValue(selectedRegistration)
  )
  useEffect(() => {
    setCurrentUser(getDefaultValue(selectedRegistration))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRegistration?.id])

  const [users, setUsers] = useState<(Options & User)[]>([])
  const fetch = useMemo(
    () =>
      debounce((request: string | null) => {
        if (!request || lastArg.originalArgs === request) return

        setLoading(true)
        searchUser(request)
          .then(data => {
            setUsers(
              data.data?.map(user => ({
                ...user,
                value: String(user.id),
                label: `${user.last_name} ${user.first_name}`,
              })) ?? []
            )
            setLoading(false)
          })
          .catch(() => {
            setLoading(false)
          })
      }, 600),
    [searchUser, lastArg]
  )

  const handleClose = () => {
    navigate(`/admin/details/${eventId}/participants?${searchParams.toString()}`)
  }

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.target as HTMLFormElement)
    const status =
      getOption(participantStatusOptions, formData.get('status') as string)?.value ??
      ('' as ParticipantStatus.PendingCheckout)

    if (status === ParticipantStatus.Cancelled) {
      toast.error(t('Impossible de mettre à jour le statut d’inscription à annulé'))
      return
    }

    if (isNew && !currentUser) {
      toast.info(t('Veuillez sélectionner un utilisateur'))
      return
    }

    const parsedData:
      | Pick<Participants, 'status' | 'id'>
      | Pick<Participants, 'status' | 'present'> = isNew
      ? {
          id: currentUser!.id,
          status,
        }
      : {
          status,
          present: (formData.get('present') as string) === 'on',
        }

    // TODO use zod validation and schema
    submit(parsedData, {
      method: 'POST',
      encType: 'application/json',
      action: `/admin/details/${eventId}/participants/details/${currentParticipant}?${searchParams.toString()}`,
    })
  }

  return (
    <Modal open={true} onClose={handleClose}>
      <Stack sx={style}>
        <Form onSubmit={onSubmitForm}>
          <Stack>
            <Title>{isNew ? t('Ajouter une incription') : t('Modifier une incription')}</Title>
            <Divider />
          </Stack>
          <Stack gap="1rem" flex="1 0" margin="1.5rem 0">
            {isNew && <h6>{t("L'inscription sera générée sans paiement")}</h6>}
            <Autocomplete
              disablePortal
              id="user-autocomplete"
              options={users}
              readOnly={!isNew}
              popupIcon={!isNew ? null : undefined}
              filterOptions={x => x}
              value={currentUser}
              onChange={(_, opts) => {
                setCurrentUser(opts ?? undefined)
              }}
              onInputChange={(_, newInputValue) => {
                fetch(newInputValue)
              }}
              loading={loading}
              renderInput={params => (
                <TextField
                  {...params}
                  label={t('Utilisateur')}
                  name={'user'}
                  disabled={!isNew}
                  variant={!isNew ? 'filled' : 'outlined'}
                />
              )}
            />
            <Autocomplete
              disablePortal
              id="status-autocomplete"
              options={participantStatusOptions}
              fullWidth
              disableClearable
              defaultValue={
                getOption(participantStatusOptions, undefined, selectedRegistration?.status) ??
                undefined
              }
              renderInput={params => (
                <TextField {...params} label={t("Statut d'inscription")} name="status" />
              )}
            />
            {currentParticipant !== 'new' && (
              <FormControlLabel
                defaultChecked={!!selectedRegistration?.present}
                name="present"
                control={<Checkbox defaultChecked={!!selectedRegistration?.present} />}
                label={t('Présent')}
              />
            )}
          </Stack>
          <Stack direction="row" justifyContent="space-between" paddingBottom="1.5rem">
            <Button
              onClick={e => {
                e.preventDefault()
                handleClose()
              }}
            >
              {t('Annuler')}
            </Button>
            <LoadingButton variant="contained" type="submit">
              {isNew ? t('Ajouter') : t('Modifier')}
            </LoadingButton>
          </Stack>
        </Form>
      </Stack>
    </Modal>
  )
}
